import React, { useEffect } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/es";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.locale("es");

const { RangePicker } = DatePicker;

const PermissionFormEditCreate = ({ formik, isPartner, isCreate }) => {
  const calculateDaysRequested = (
    start: dayjs.Dayjs | null,
    end: dayjs.Dayjs | null
  ) => {
    if (start && end) {
      const difference = end.diff(start, "day");
      return difference + 1;
    }
    return 0;
  };

  const hasExceededMaxDays = () => {
    if (formik.values.startRequested && formik.values.endRequested) {
      const startDate = dayjs.unix(formik.values.startRequested);
      const endDate = dayjs.unix(formik.values.endRequested);
      const daysDiff = endDate.diff(startDate, 'day') + 1;
      return daysDiff > 7;
    }
    return false;
  };

  React.useEffect(() => {
    if (formik.setFieldValue && typeof formik.setFieldValue === 'function') {
      formik.setFieldValue('hasExceededMaxDays', hasExceededMaxDays());
    }
  }, [formik.values.startRequested, formik.values.endRequested]);

  const handleDateChange = (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null]
  ) => {
    const [start, end] = dates;

    formik.setFieldValue("startRequested", start ? start.unix() : null);
    formik.setFieldValue("endRequested", end ? end.unix() : null);
    const daysRequested = calculateDaysRequested(start, end);
    formik.setFieldValue("daysRequested", daysRequested);
  };

  useEffect(() => {
    if (formik.values.startRequested && formik.values.endRequested) {
      const startDate = dayjs.unix(formik.values.startRequested);
      const endDate = dayjs.unix(formik.values.endRequested);

      formik.setFieldValue("dateRange", [startDate, endDate]);
    }
  }, [formik.values.startRequested, formik.values.endRequested]);

  const getMinimumDate = () => {
    return dayjs().add(1, "day").toDate();
  };

  const getMaximumDate = () => {
    return dayjs().add(3, "months").toDate();
  };

  return (
    <div className="p-4">
      <div className="row">
        <div className="col-md-12 mb-3">
          <label className="form-label">Descripción *</label>
          <textarea
            name="description"
            className="form-control"
            rows="4"
            placeholder="Describe el motivo del permiso..."
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.description && formik.errors.description && (
            <div className="text-danger">{formik.errors.description}</div>
          )}
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label">Rango de fechas del permiso *</label>
          <RangePicker
            value={formik.values.dateRange}
            onChange={handleDateChange}
            format="DD/MM/YYYY"
            placeholder={["Fecha inicio", "Fecha fin"]}
            disabledDate={(current) => {
              return current && current < getMinimumDate();
            }}
            style={{
              width: "100%",
              borderColor: formik.touched.startRequested && formik.errors.startRequested ? "red" : undefined,
            }}
          />
          {formik.touched.startRequested && formik.errors.startRequested && (
            <div className="text-danger small mt-1">
              {formik.errors.startRequested}
            </div>
          )}
          <small className="form-text text-muted">
            Mínimo 1 día de antelación, máximo 7 días
          </small>
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label">Días Solicitados</label>
          <input
            type="number"
            name="daysRequested"
            className="form-control"
            value={formik.values.daysRequested || 0}
            disabled
          />
          <small className="form-text text-muted">
            Máximo 7 días (se calcula automáticamente)
          </small>
        </div>

        {/* Validaciones adicionales */}
        {formik.values.startRequested && formik.values.endRequested && (
          <div className="col-md-12">
            <div className="alert alert-info small mt-2">
              {(() => {
                const startDate = dayjs.unix(formik.values.startRequested);
                const endDate = dayjs.unix(formik.values.endRequested);
                
                if (startDate.isAfter(endDate)) {
                  return (
                    <div className="alert alert-danger small mt-2">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      La fecha de inicio no puede ser posterior a la fecha de fin
                    </div>
                  );
                }
                
                if (startDate.isBefore(dayjs().add(1, 'day'))) {
                  return (
                    <div className="alert alert-warning small mt-2">
                      <i className="fas fa-info-circle me-2"></i>
                      El permiso debe solicitarse con al menos 1 día de antelación
                    </div>
                  );
                }
                
                const daysDiff = endDate.diff(startDate, 'day') + 1;
                if (daysDiff > 7) {
                  return (
                    <div className="alert alert-danger small mt-2">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      El permiso no puede exceder 7 días
                    </div>
                  );
                }
                
                return (
                  <div className="alert alert-success small mt-2">
                    <i className="fas fa-check-circle me-2"></i>
                    Rango de fechas válido ({daysDiff} día{daysDiff > 1 ? 's' : ''})
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PermissionFormEditCreate; 